import { DirectionalHeatmapControl } from "../controls/DirectionalHeatmapControl";
import { DataSourceControl } from "../controls/DataSourceControl";
import {
  getDirectionalLayers,
  SPEED_BREAKPOINTS,
} from "../layers/DirectionalLayers";
import {
  attachMotionRecolor,
  createMotionOverlayLayer,
} from "../layers/DirectionalMotion";
import { interpolatedSpeedFromColor } from "../layers/style/util/colorScale";
import {
  registerHeatmapControl,
  registerDataSourceControl,
  getFCDToggleFromURL,
  getFCDModeFromURL,
  getFCDDirectionsFromURL,
  getFCDDataSourceFromURL,
} from "../state/URLStateManager";

export function addHeatmapLayersAndControls(
  map,
  azureLoginControl,
  dateInputControl,
  target,
  layerGroup,
  featureInfoControl,
) {
  // Heatmap layer Controls
  const directionalHeatmapControl = new DirectionalHeatmapControl({
    target: target,
  });
  const dataSourceControl = new DataSourceControl({
    target: target,
  });
  map.addControl(directionalHeatmapControl);
  map.addControl(dataSourceControl);

  directionalHeatmapControl.restoreState(
    getFCDToggleFromURL(),
    getFCDModeFromURL(),
    getFCDDirectionsFromURL(),
  );
  registerHeatmapControl(directionalHeatmapControl);
  // Main viewer: the Overlays section shows FCD's options only while it is on
  // (CSS on `.sidebar-section-body.fcd-off`). Applied after the URL restore too.
  const syncFcdOff = () =>
    target.classList.toggle(
      "fcd-off",
      directionalHeatmapControl.get("heatmapToggle") !== true,
    );
  syncFcdOff();
  directionalHeatmapControl.on("change:heatmapToggle", syncFcdOff);

  const fcdDataSource = getFCDDataSourceFromURL();
  if (fcdDataSource != null) {
    dataSourceControl.set("dataSource", fcdDataSource);
  }
  registerDataSourceControl(dataSourceControl);

  // Created once: AzureLoginControl refreshes the token every few minutes and
  // the WMS loader reads it per request, so a refresh must not rebuild layers.
  let directionalLayers;
  azureLoginControl.on("propertychange", function () {
    const authorized =
      azureLoginControl.get("idToken") &&
      azureLoginControl.get("idTokenClaims") &&
      azureLoginControl.get("idTokenClaims").roles &&
      azureLoginControl.get("idTokenClaims").roles.includes("FCD.Reader");
    if (authorized && !directionalLayers) {
      directionalLayers = getDirectionalLayers(
        dateInputControl,
        azureLoginControl,
        dataSourceControl,
        directionalHeatmapControl,
      );
      directionalHeatmapControl.set("directionalLayers", directionalLayers);
      const isMotionMode = () =>
        directionalHeatmapControl.get("displayMode") === "motion";
      directionalLayers.forEach(function (layer) {
        attachMotionRecolor(layer, isMotionMode);
        layerGroup.getLayers().push(layer);
      });
      layerGroup
        .getLayers()
        .push(createMotionOverlayLayer(directionalLayers, map, isMotionMode));
    }
  });
  map.on("moveend", function () {
    directionalHeatmapControl.set("zoom", map.getView().getZoom());
    dataSourceControl.set("zoom", map.getView().getZoom());
  });

  let hoverFrame = null;
  let lastSpeedsKey = null;
  map.on("pointermove", function (e) {
    if (e.dragging || e.originalEvent.target.tagName !== "CANVAS") return;
    if (hoverFrame !== null) return;
    hoverFrame = requestAnimationFrame(function () {
      hoverFrame = null;
      // Motion mode also renders the WMS speed image, so it can be sampled.
      const mode = directionalHeatmapControl.get("displayMode");
      const inSpeedMode = mode === "speed" || mode === "motion";
      const fcdOn = directionalHeatmapControl.get("heatmapToggle") === true;
      const active = directionalHeatmapControl.get("active") === true;
      let speeds = null;
      if (inSpeedMode && fcdOn && active && directionalLayers) {
        const sampled = {};
        directionalLayers.forEach(function (layer, id) {
          if (!layer.getVisible() || layer.getOpacity() === 0) return;
          const data = layer.getData(e.pixel);
          if (!data || data[3] === 0) return;
          sampled[id] = interpolatedSpeedFromColor(
            [data[0], data[1], data[2]],
            SPEED_BREAKPOINTS,
          );
        });
        if (Object.keys(sampled).length > 0) speeds = sampled;
      }
      const key =
        speeds === null
          ? null
          : Object.keys(speeds)
              .map(Number)
              .sort((a, b) => a - b)
              .map((id) => id + ":" + speeds[id])
              .join(",");
      if (key === lastSpeedsKey) return;
      lastSpeedsKey = key;
      featureInfoControl.set("fcdSpeeds", speeds);
    });
  });
}
